import { useSearchParams } from 'next/navigation';
import { useState } from 'react';
import { MessageDescriptor, useIntl } from 'react-intl';

import {
	CalmGradients,
	FocusRingVariants,
	FontSizes,
	FontWeights,
	Loader,
	QuestionnaireTile,
	Text,
} from '@calm-web/design-system';

import { SelectedTiles } from '@/components/PreSignupFlow/PreSignupProvider/types';
import { Tile } from '@/components/ui/QuestionnaireTile/types';
import { useAnalytics } from '@/hooks/analytics';
import { useLocaleState, useUserState } from '@/hooks/store';
import { useAmplitudeExperiment } from '@/hooks/utils/experiments/useAmplitudeExperiment';
import { useRouterPush } from '@/hooks/utils/useRouterPush';
import genericMessages from '@/messages/messages';
import { postQuestionnairePoll } from '@/utils/endpoints';
import { userCanTrial } from '@/utils/subscriptions';

import { onboardingTiles } from './data';
import messages from './messages';
import { ContinueButton, OnboardingTiles, OnboardingTilesGroup } from './styles';

const initSelectedGoals = (queryGoals: string) => {
	return queryGoals.split(',').reduce((accu, curr) => {
		return {
			...accu,
			[curr]: {
				id: curr,
			},
		};
	}, {});
};

const OnboardingQuestionnaire = () => {
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const searchParams = useSearchParams();
	const user = useUserState();
	const locale = useLocaleState();
	const routerPush = useRouterPush();
	const { isEnrolled, isLoading } = useAmplitudeExperiment('www-ua-homepage-questionnaire');

	const [isRedirecting, setIsRedirecting] = useState(false);
	const goals = searchParams?.get('goals');
	const [selectedTiles, setSelectedTiles] = useState<SelectedTiles>(() => {
		if (!goals || typeof goals !== 'string') {
			return {};
		}
		return initSelectedGoals(goals);
	});

	if (user && !userCanTrial(user)) return null;

	const isSelected = (tileId: string) => {
		return !!selectedTiles[tileId];
	};

	const onTileClick = (tile: Tile) => {
		const { id } = tile;
		const newSelectedTiles = { ...selectedTiles, [id]: tile };

		if (isSelected(id)) {
			delete newSelectedTiles[id];
		}

		setSelectedTiles(newSelectedTiles);
	};

	const onContinue = async () => {
		setIsRedirecting(true);

		const selectedTilesArr = Object.keys(selectedTiles)
			.map(i => selectedTiles[i])
			.filter(i => i);

		const answers = selectedTilesArr.map(({ id }) => id);

		await postQuestionnairePoll([
			{
				poll_name: 'goals_web_stress_onboarding',
				question: 'What can we help with today?',
				answers,
			},
		]);

		logEvent({
			eventName: 'FTUE : Goal Questionnaire : Proceeded',
			eventProps: {
				choices: answers,
				num_choices: selectedTilesArr?.length,
			},
		});

		logEvent({
			eventName: 'Landing Page : Signup Flow CTA : Clicked',
		});

		const querifiedAnswers = answers.join();

		// we do not want calm_to_work to influence the user flow
		const answersToDetermineFlow = answers.filter(answer => answer !== 'calm_to_work');
		const isSleepQuality =
			answersToDetermineFlow.includes('sleep_quality') && answersToDetermineFlow.length === 1;
		const findFocusParam = (isSleepQuality: boolean) => {
			const focus = isSleepQuality ? 'sleep' : 'stress';
			return isEnrolled ? `${focus}_questionnaireB&step=q1` : `${focus}`;
		};

		await routerPush('signup-flow', {
			focus: findFocusParam(isSleepQuality),
			goals: querifiedAnswers,
		});
	};

	const titleId = 'onboarding-goals-title';

	if (isLoading) return <Loader />;

	return (
		<>
			<Text
				noMargin
				el="h3"
				size={locale === 'ja' ? FontSizes.base : FontSizes.lg}
				weight={FontWeights.Medium}
				id={titleId}
			>
				{formatMessage(messages.questionnaireCta)}
			</Text>
			<OnboardingTilesGroup role="group" aria-labelledby={titleId}>
				<OnboardingTiles>
					{onboardingTiles.map((tile, i) => (
						<li key={tile.id}>
							<QuestionnaireTile
								colors={{
									color1: CalmGradients[i][0],
									color2: CalmGradients[i][1],
								}}
								title={formatMessage(tile.title as MessageDescriptor)}
								onClick={(): void => onTileClick(tile)}
								isSelected={isSelected(tile.id)}
								Icon={tile.Icon}
								focusRingVariant={FocusRingVariants.Light}
								role="checkbox"
							/>
						</li>
					))}
				</OnboardingTiles>
			</OnboardingTilesGroup>
			<ContinueButton
				fullWidth
				onPress={onContinue}
				backgroundColor="white"
				textColor="gray6"
				isLoading={isRedirecting}
				isActive={Object.keys(selectedTiles).length > 0}
				loaderColor="gray6"
			>
				{formatMessage(genericMessages.continue)}
			</ContinueButton>
		</>
	);
};

export default OnboardingQuestionnaire;
